// frontend/src/pages/Reportes.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box,Container, Paper, Stack, TextField, Button, Tabs, Tab, Grid, Typography, FormControl, InputLabel, Select, MenuItem, LinearProgress, Chip } from '@mui/material';
import DataTable from '../components/DataTable';
import {
  fetchSalesReport, downloadSalesCsv,fetchPurchasesSummary,
  fetchKpis, fetchSalesByDay, fetchSalesByProduct, fetchSalesByCategory,
  fetchSalesByClient, fetchSalesByUser,
  fetchInventory, fetchLowStock,
  downloadInventoryCsv, downloadLowStockCsv,
  downloadCsvGeneric,
  fetchProductsReport, downloadProductsReportExcel
} from '../api/reports';
import { fetchCategoriasPadre } from '../api/categorias';
import { fetchRentabilidad } from '../api/finanzas';
import { useLoading } from '../contexts/LoadingContext';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, PieChart, Pie, Legend } from 'recharts';

const currency = (n) => (n==null ? '—' : new Intl.NumberFormat('es-SV',{style:'currency',currency:'USD',maximumFractionDigits:2}).format(Number(n)));

function KpiCard({ title, value }) {
  return (
    <Paper sx={{ p:2, borderRadius:3, boxShadow:3 }}>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ mt:0.5 }}>{value ?? '—'}</Typography>
    </Paper>
  );
}

export default function Reportes() {
  const { withLoader, stop } = useLoading();
  const reqIdRef = useRef(0);
  const mountedRef = useRef(false);
  const didMount = useRef(false);

  const SAFE_TIMEOUT_MS = 20000;
  const withTimeout = (promise, ms, label='task') =>
    new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error(`Timeout ${label} después de ${ms}ms`)), ms);
      promise.then(
        v => { clearTimeout(t); resolve(v); },
        e => { clearTimeout(t); reject(e); }
      );
    });
  const logSettledErrors = (results, scope) => {
    const errs = results.filter(r => r.status === 'rejected');
    if (errs.length) console.error(`[Reportes] Errores en ${scope}:`, errs.map(e => e.reason));
  };

  // filtros
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [estado, setEstado] = useState('finalizada'); // ✅ ahora filtra por estado_venta
  const [categoriaId, setCategoriaId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const TZ = '-06:00';
//compras
const [purchases, setPurchases] = useState(null);

  // ventas
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // datasets
  const [kpis, setKpis] = useState(null);
  const [byDay, setByDay] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [byClient, setByClient] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [profitability, setProfitability] = useState({ rows: [], summary: {} });

  // inventario
  const [inventoryRows, setInventoryRows] = useState([]);
  const [invPage, setInvPage] = useState(1);
  const [invPageSize] = useState(50);
  const [invTotal, setInvTotal] = useState(0);

  // bajo stock
  const [lowRows, setLowRows] = useState([]);
  const [lowPage, setLowPage] = useState(1);
  const [lowPageSize] = useState(50);
  const [lowTotal, setLowTotal] = useState(0);
  const [threshold, setThreshold] = useState(2);

  // reporte de productos
  const [prodReportRows, setProdReportRows] = useState([]);
  const [prodReportPage, setProdReportPage] = useState(1);
  const [prodReportPageSize] = useState(50);
  const [prodReportTotal, setProdReportTotal] = useState(0);

  const [tab, setTab] = useState('ventas');

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(new Blob([blob], { type:'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    
  };
  const paramsAgg = () => ({ from: from||undefined, to: to||undefined, estado, tz: TZ, categoriaId: categoriaId||undefined });

  // cargas sin overlay
  const loadSales = async (rid) => {
    const r = await fetchSalesReport({ page, pageSize, ...paramsAgg() });
    if (rid && rid !== reqIdRef.current) return;
    setRows(r.rows); setTotal(r.total);
  };
  const loadInventory = async (rid) => {
    const r = await fetchInventory({ page: invPage, pageSize: invPageSize });
    if (rid && rid !== reqIdRef.current) return;
    setInventoryRows(r.rows); setInvTotal(r.total);
  };
  const loadLowStock = async (rid) => {
    const r = await fetchLowStock({ threshold, page: lowPage, pageSize: lowPageSize });
    if (rid && rid !== reqIdRef.current) return;
    setLowRows(r.rows); setLowTotal(r.total);
  };
  const loadProductsReport = async (rid) => {
    const r = await fetchProductsReport({ page: prodReportPage, pageSize: prodReportPageSize });
    if (rid && rid !== reqIdRef.current) return;
    setProdReportRows(r.rows); setProdReportTotal(r.total);
  };
  const loadProfitability = async (rid) => {
    const result = await fetchRentabilidad({
      from: from || undefined,
      to: to || undefined,
      categoriaId: categoriaId || undefined,
      tz: TZ,
    });
    if (rid && rid !== reqIdRef.current) return;
    setProfitability(result || { rows: [], summary: {} });
  };
  const reloadAgg = async (rid) => {
    const p = paramsAgg();
    const res = await Promise.allSettled([
      fetchKpis(p), fetchSalesByDay(p), fetchSalesByProduct(p),
      fetchSalesByCategory(p), fetchSalesByClient(p), fetchSalesByUser(p),fetchPurchasesSummary({ from: p.from, to: p.to, estado: 'recibida', tz: p.tz, categoriaId: p.categoriaId }),
    ]);
    if (rid && rid !== reqIdRef.current) return;
    const [k, d, pr, cat, cli, usr,pur] = res.map(r => (r.status === 'fulfilled' ? r.value : undefined));
    setPurchases(pur ?? null);
    setKpis(k ?? null);
    setByDay(d ?? []);
    setByProduct(pr ?? []);
    setByCategory(cat ?? []);
    setByClient(cli ?? []);
    setByUser(usr ?? []);
    logSettledErrors(res, 'reloadAgg');
  };

  // carga inicial con overlay
  const loadAll = async () => {
    const rid = ++reqIdRef.current;
    try {
      await withLoader(async () => {
        const results = await Promise.allSettled([
          withTimeout(loadSales(rid), SAFE_TIMEOUT_MS, 'ventas'),
          withTimeout(reloadAgg(rid), SAFE_TIMEOUT_MS, 'agregados'),
          withTimeout(loadInventory(rid), SAFE_TIMEOUT_MS, 'inventario'),
          withTimeout(loadLowStock(rid), SAFE_TIMEOUT_MS, 'bajo_stock'),
          withTimeout(loadProductsReport(rid), SAFE_TIMEOUT_MS, 'productos'),
          withTimeout(loadProfitability(rid), SAFE_TIMEOUT_MS, 'rentabilidad'),
          withTimeout(fetchCategoriasPadre().then((r) => setCategorias(r.data ?? [])), SAFE_TIMEOUT_MS, 'categorias'),
        ]);
        logSettledErrors(results, 'loadAll');
      });
    } finally {
      stop?.();
    }
  };

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    stop?.();
    loadAll(); // ✅ carga inicial automática
  }, []);

  useEffect(() => { if (!didMount.current) { didMount.current = true; return; } loadSales(); }, [page]);
  useEffect(() => { loadInventory(); }, [invPage]);
  useEffect(() => { loadLowStock(); }, [lowPage, threshold]);
  useEffect(() => { loadProductsReport(); }, [prodReportPage]);

  const onApply = async () => {
    setPage(1); setInvPage(1); setLowPage(1);
    await loadAll();
  };

  const columns = useMemo(()=>[
    { Header:'ID', accessor:'id' },
    { Header:'Código', accessor:'codigo' },
    { Header:'Fecha', accessor:'fecha' },
    { Header:'Total', accessor:'total_venta', Cell: ({value}) => currency(value) },
    { Header:'Ganancia neta', accessor:'ganancia_neta', Cell: ({value}) => currency(value) },
    { Header:'Cliente', accessor:'cliente' },
    { Header:'Usuario', accessor:'usuario' },
  ],[]);

  const Presets = () => (
    <Stack direction="row" spacing={1}>
      <Button size="small" onClick={()=>{ const t=new Date().toISOString().slice(0,10); setFrom(t); setTo(t); }}>Hoy</Button>
      <Button size="small" onClick={()=>{ const d=new Date(); d.setDate(d.getDate()-6); setFrom(d.toISOString().slice(0,10)); setTo(new Date().toISOString().slice(0,10)); }}>7d</Button>
      <Button size="small" onClick={()=>{ const d=new Date(); d.setDate(d.getDate()-29); setFrom(d.toISOString().slice(0,10)); setTo(new Date().toISOString().slice(0,10)); }}>30d</Button>
      <Button size="small" onClick={()=>{ setFrom(''); setTo(''); }}>Clear</Button>
    </Stack>
  );

  return (
    <Container sx={{ mt:4, pb:4 }}>
      {/* KPIs */}
      {tab !== 'rentabilidad' && (
        <>
          <Grid container spacing={2} sx={{ mb:2 }}>
            <Grid item xs={6} md={2.4}><KpiCard title="Total ventas" value={currency(kpis?.total_ventas)} /></Grid>
            <Grid item xs={6} md={2.4}><KpiCard title="Tickets" value={kpis?.tickets} /></Grid>
            <Grid item xs={6} md={2.4}><KpiCard title="Ticket promedio" value={currency(kpis?.ticket_promedio)} /></Grid>
            <Grid item xs={6} md={2.4}><KpiCard title="Clientes únicos" value={kpis?.clientes_unicos} /></Grid>
            <Grid item xs={6} md={2.4}><KpiCard title="Ganancia total" value={currency(kpis?.ganancia_total)} /></Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb:2 }}>
            <Grid item xs={6} md={4}>
              <KpiCard title="Total compras (recibidas)" value={currency(purchases?.total_compras)} />
            </Grid>
            <Grid item xs={6} md={4}>
              <KpiCard title="Total libraje" value={currency(purchases?.total_libraje)} />
            </Grid>
            <Grid item xs={6} md={4}>
              <KpiCard title="Compras + libraje" value={currency(purchases?.total_compras_mas_libraje)} />
            </Grid>
          </Grid>
        </>
      )}

      {/* Toolbar filtros */}
      {/* Toolbar filtros */}
<Paper sx={{ p:2, mb:2, position:'sticky', top:64, zIndex:1 }}>
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
    }}
  >
    {/* IZQUIERDA: filtros (pueden envolver) */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        flex: '1 1 auto',
        minWidth: 260,
      }}
    >
      <TextField
        label="Desde"
        type="date"
        size="small"
        InputLabelProps={{ shrink: true }}
        value={from}
        onChange={(e)=>setFrom(e.target.value)}
      />

      <TextField
        label="Hasta"
        type="date"
        size="small"
        InputLabelProps={{ shrink: true }}
        value={to}
        onChange={(e)=>setTo(e.target.value)}
      />

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Estado de venta</InputLabel>
        <Select label="Estado de venta" value={estado} onChange={(e)=>setEstado(e.target.value)}>
          <MenuItem value="finalizada">Finalizada</MenuItem>
          <MenuItem value="activa">Activa</MenuItem>
          <MenuItem value="cancelada">Cancelada</MenuItem>
          <MenuItem value="todas">Todas</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>CategorÃ­a</InputLabel>
        <Select label="CategorÃ­a" value={categoriaId} onChange={(e)=>setCategoriaId(e.target.value)}>
          <MenuItem value="">Todas</MenuItem>
          {categorias.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Presets />
    </Box>

    {/* DERECHA: acciones (no se rompen) */}
    <Stack
      direction="row"
      spacing={1}
      sx={{
        flex: '0 0 auto',
        ml: { sm: 'auto' },
        width: { xs: '100%', sm: 'auto' },
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
      }}
    >
      <Button variant="contained" onClick={onApply} sx={{ whiteSpace:'nowrap' }}>
        Aplicar
      </Button>

      <Button
        variant="outlined"
        sx={{ whiteSpace:'nowrap' }}
        onClick={async ()=>{
          await withLoader(async ()=>{
            const blob = await withTimeout(downloadSalesCsv({ ...paramsAgg() }), SAFE_TIMEOUT_MS, 'csv ventas');
            triggerDownload(blob, 'sales_report.csv');
          });
          stop?.();
        }}
      >
        Exportar CSV
      </Button>

      <Button
        variant="outlined"
        sx={{ whiteSpace:'nowrap' }}
        onClick={async ()=>{
          await withLoader(async ()=>{
            const p = paramsAgg();
            const blob = await withTimeout(
              downloadCsvGeneric('/purchases-details.csv', {
                from: p.from,
                to: p.to,
                estado: 'recibida',
                tz: p.tz,
                categoriaId: p.categoriaId,
              }),
              SAFE_TIMEOUT_MS,
              'csv compras'
            );
            triggerDownload(blob, 'purchases_details.csv');
          });
          stop?.();
        }}
      >
        CSV Compras
      </Button>
    </Stack>
  </Box>
</Paper>


      {/* Tabs */}
      <Paper sx={{ mb:2 }}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} variant="scrollable" scrollButtons allowScrollButtonsMobile>
          <Tab label="VENTAS" value="ventas" />
          <Tab label="POR DÍA" value="dia" />
          <Tab label="POR PRODUCTO" value="prod" />
          <Tab label="POR CATEGORÍA" value="cat" />
          <Tab label="POR CLIENTE" value="cli" />
          <Tab label="POR USUARIO" value="usr" />
          <Tab label="INVENTARIO" value="inv" />
          <Tab label="BAJO STOCK" value="low" />
          <Tab label="PRODUCTOS" value="productos" />
          <Tab label="RENTABILIDAD" value="rentabilidad" />
        </Tabs>
      </Paper>

      {/* Ventas */}
      {tab==='ventas' && (
        <Paper>
          <DataTable
            rows={rows}
            columns={columns}
            pagination={{ page, pageSize, total, onPageChange:setPage }}
            showActions={false}
          />
        </Paper>
      )}

      {/* Por día */}
      {tab==='dia' && (
        <Paper sx={{ p:2 }}>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={byDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="total" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Por producto */}
      {tab==='prod' && (
        <Paper sx={{ p:2 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb:1 }}>
            <Button size="small" onClick={async ()=>{
              await withLoader(async ()=>{
                const blob = await withTimeout(downloadCsvGeneric('/sales-by-product.csv', paramsAgg()), SAFE_TIMEOUT_MS, 'csv por producto');
                triggerDownload(blob, 'sales_by_product.csv');
              });
              stop?.();
            }}>CSV</Button>
          </Stack>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={byProduct}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" hide />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="ingreso" name="Ingreso" />
              <Bar dataKey="ganancia" name="Ganancia" /> {/* ✅ si backend la devuelve */}
              <Bar dataKey="unidades" name="Unidades" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Por categoría */}
      {tab==='cat' && (
        <Paper sx={{ p:2 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb:1 }}>
            <Button size="small" onClick={async ()=>{
              await withLoader(async ()=>{
                const blob = await withTimeout(downloadCsvGeneric('/sales-by-category.csv', paramsAgg()), SAFE_TIMEOUT_MS, 'csv por categoría');
                triggerDownload(blob, 'sales_by_category.csv');
              });
              stop?.();
            }}>CSV</Button>
          </Stack>
          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Tooltip />
              <Legend />
              <Pie data={byCategory} dataKey="ingreso" nameKey="categoria" outerRadius={120} />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Por cliente */}
      {tab==='cli' && (
        <Paper sx={{ p:2 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb:1 }}>
            <Button size="small" onClick={async ()=>{
              await withLoader(async ()=>{
                const blob = await withTimeout(downloadCsvGeneric('/sales-by-client.csv', paramsAgg()), SAFE_TIMEOUT_MS, 'csv por cliente');
                triggerDownload(blob, 'sales_by_client.csv');
              });
              stop?.();
            }}>CSV</Button>
          </Stack>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={byClient}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="cliente" hide />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="ingreso" name="Ingreso" />
              <Bar dataKey="ganancia" name="Ganancia" />
              <Bar dataKey="tickets" name="Tickets" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Por usuario */}
      {tab==='usr' && (
        <Paper sx={{ p:2 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb:1 }}>
            <Button size="small" onClick={async ()=>{
              await withLoader(async ()=>{
                const blob = await withTimeout(downloadCsvGeneric('/sales-by-user.csv', paramsAgg()), SAFE_TIMEOUT_MS, 'csv por usuario');
                triggerDownload(blob, 'sales_by_user.csv');
              });
              stop?.();
            }}>CSV</Button>
          </Stack>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={byUser}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="usuario" hide />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="ingreso" name="Ingreso" />
              <Bar dataKey="ganancia" name="Ganancia" />
              <Bar dataKey="tickets" name="Tickets" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Inventario */}
      {tab==='inv' && (
        <Paper sx={{ p:2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
            <Typography variant="h6">Inventario (valuación)</Typography>
            <Button size="small" onClick={async ()=>{
              await withLoader(async ()=>{
                const blob = await withTimeout(downloadInventoryCsv(), SAFE_TIMEOUT_MS, 'csv inventario');
                triggerDownload(blob, 'inventory.csv');
              });
              stop?.();
            }}>CSV</Button>
          </Stack>
          <DataTable
            rows={inventoryRows}
            columns={[
              { Header:'ID', accessor:'id' },
              { Header:'Producto', accessor:'nombre' },
              { Header:'Stock', accessor:'stock' },
              { Header:'Precio', accessor:'precio_venta', Cell: ({value})=>currency(value) },
              { Header:'Costo prom.', accessor:'costo_promedio', Cell: ({value})=>currency(value) },
              { Header:'Valuación', accessor:'valuacion', Cell: ({value})=>currency(value) },
            ]}
            pagination={{ page: invPage, pageSize: invPageSize, total: invTotal, onPageChange:setInvPage }}
            showActions={false}
          />
        </Paper>
      )}

      {/* Bajo stock */}
      {tab==='low' && (
        <Paper sx={{ p:2 }}>
          <Stack direction={{xs:'column', sm:'row'}} spacing={2} alignItems="center" sx={{ mb:1 }}>
            <Typography variant="h6" sx={{ flexGrow:1 }}>Bajo stock (≤ {threshold})</Typography>
            <TextField
              label="Umbral"
              type="number"
              size="small"
              value={threshold}
              onChange={(e)=>{ setLowPage(1); setThreshold(Math.max(0, Number(e.target.value)||0)); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 120 }}
            />
            <Button size="small" onClick={async ()=>{
              await withLoader(async ()=>{
                const blob = await withTimeout(downloadLowStockCsv({ threshold }), SAFE_TIMEOUT_MS, 'csv bajo stock');
                triggerDownload(blob, 'low_stock.csv');
              });
              stop?.();
            }}>CSV</Button>
          </Stack>
          <DataTable
            rows={lowRows}
            columns={[
              { Header:'ID', accessor:'id' },
              { Header:'Producto', accessor:'nombre' },
              { Header:'Stock', accessor:'stock' },
            ]}
            pagination={{ page: lowPage, pageSize: lowPageSize, total: lowTotal, onPageChange:setLowPage }}
            showActions={false}
          />
        </Paper>
      )}
      {/* Reporte de productos */}
      {tab==='productos' && (
        <Paper sx={{ p:2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
            <Typography variant="h6">Productos — Categoría Skincare</Typography>
            <Button size="small" onClick={async ()=>{
              await withLoader(async ()=>{
                const blob = await withTimeout(downloadProductsReportExcel(), SAFE_TIMEOUT_MS, 'excel productos');
                const url = URL.createObjectURL(new Blob([blob], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                const a = document.createElement('a'); a.href = url; a.download = 'products_report.xlsx'; a.click(); URL.revokeObjectURL(url);
              });
              stop?.();
            }}>Excel</Button>
          </Stack>
          <DataTable
            rows={prodReportRows}
            columns={[
              { Header:'ID', accessor:'id' },
              { Header:'SKU', accessor:'sku' },
              { Header:'Nombre', accessor:'nombre' },
              { Header:'Marca', accessor:'marca' },
              { Header:'Categoría', accessor:'categoria' },
              { Header:'Subcategoría', accessor:'subcategoria' },
              { Header:'Presentación', accessor:'presentacion' },
              { Header:'Libraje', accessor:'libraje' },
              { Header:'Stock', accessor:'stock' },
              { Header:'Precio venta', accessor:'precio_venta', Cell: ({value})=>currency(value) },
              { Header:'Precio compra + libraje (reciente)', accessor:'precio_compra_reciente', Cell: ({value})=>value!=null ? currency(value) : '—' },
              { Header:'Fecha última compra', accessor:'fecha_ultima_compra', Cell: ({value})=>value ? new Date(value).toLocaleDateString('es-SV') : '—' },
            ]}
            pagination={{ page: prodReportPage, pageSize: prodReportPageSize, total: prodReportTotal, onPageChange: setProdReportPage }}
            showActions={false}
          />
        </Paper>
      )}
      {tab==='rentabilidad' && (
        <Stack spacing={2}>
          <Alert severity="info">
            La ganancia del sistema usa ventas finalizadas. La publicidad se prorratea por día y los retiros se descuentan según su fecha y categoría.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard title="Ganancia manual" value={currency(profitability.summary?.ganancia_manual)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard title="Publicidad aplicada" value={currency(profitability.summary?.publicidad)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard title="Ganancia operativa" value={currency(profitability.summary?.ganancia_operativa)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard title="Donaciones y retiros" value={currency(profitability.summary?.retiros_total)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard title="Ganancia disponible final" value={currency(profitability.summary?.ganancia_disponible)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard title="Compras recibidas" value={currency(profitability.summary?.compras)} />
            </Grid>
          </Grid>
          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6">Ganancia aplicada a compras</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pendiente por cubrir: {currency(profitability.summary?.pendiente_por_cubrir)}
                </Typography>
              </Box>
              <Chip
                color={(profitability.summary?.porcentaje_cubierto || 0) >= 100 ? 'success' : 'primary'}
                label={`${Number(profitability.summary?.porcentaje_cubierto || 0).toFixed(2)}% cubierto`}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, Number(profitability.summary?.porcentaje_cubierto || 0)))}
              color={(profitability.summary?.porcentaje_cubierto || 0) >= 100 ? 'success' : 'primary'}
              sx={{ height: 10, borderRadius: 5, mb: 2 }}
            />
            <DataTable
              rows={profitability.rows || []}
              columns={[
                { Header: 'Categoría', accessor: 'categoria' },
                { Header: 'Ganancia manual', accessor: (row) => currency(row.ganancia_manual) },
                { Header: 'Ganancia bruta sistema', accessor: (row) => currency(row.ganancia_bruta) },
                { Header: 'Comisión', accessor: (row) => currency(row.comision_transportista) },
                { Header: 'Publicidad', accessor: (row) => currency(row.publicidad) },
                { Header: 'Ganancia operativa', accessor: (row) => currency(row.ganancia_operativa) },
                { Header: 'Donaciones', accessor: (row) => currency(row.retiros_donacion) },
                { Header: 'Salarios', accessor: (row) => currency(row.retiros_salario) },
                { Header: 'Otros retiros', accessor: (row) => currency(row.otros_retiros) },
                { Header: 'Disponible final', accessor: (row) => currency(row.ganancia_disponible) },
                { Header: 'Compras', accessor: (row) => currency(row.compras) },
                { Header: 'Cubierto', accessor: (row) => row.porcentaje_cubierto == null ? '—' : `${row.porcentaje_cubierto.toFixed(2)}%` },
                { Header: 'Pendiente', accessor: (row) => currency(row.pendiente_por_cubrir) },
                { Header: 'Inventario restante', accessor: (row) => currency(row.inventario_restante) },
              ]}
              showActions={false}
            />
          </Paper>
        </Stack>
      )}
    </Container>
  );
}
